import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { alpha, styled } from "@mui/material/styles";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useCancelEntityOfficerInvitationMutation } from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation, {
  EntityOfficerInvitationNotOpenError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { enqueueSnackbar } from "notistack";
import Session from "users/__session/session";

const InvitationsTable = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  position: "relative",
  width: "100%"
}));

const InvitationsTableTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));
const NoRowsPlaceholder = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
  fontSize: "1.3em",
}));
// The chip preserves the meaning of the tint for people who cannot distinguish the row color.
const InvitationRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== "sentThisVisit",
})<{ sentThisVisit: boolean }>(({ theme, sentThisVisit }) => {
  if (!sentThisVisit) return {};

  const accentColor = alpha(theme.palette.info.main, 0.95);
  return {
    "& > td": {
      backgroundColor: alpha(theme.palette.info.main, 0.07),
      borderBottomColor: accentColor,
      boxShadow: `inset 0 -2px 0 ${accentColor}`,
    },
  };
});
const InviteeEmail = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
}));

type EntityOfficerInvitationsTableProps = {
  invitations: EntityOfficerInvitation[],
  onInvitationRemoved?: (invitation: EntityOfficerInvitation) => void,
  /** Ids of invitations sent from this page since it opened. Those rows are highlighted. */
  sentThisVisitIds?: ReadonlySet<string>,
  session: Readonly<Session>
}

function ignoreRemovedInvitation() {}

export default function EntityOfficerInvitationsTable(props: Readonly<EntityOfficerInvitationsTableProps>) {
  const { invitations } = props;
  // Cancelling invalidates the open-invitation list, so a caller that renders the query result
  // needs no notification. The callback stays for one that tracks removals itself.
  const onInvitationRemoved = props.onInvitationRemoved ?? ignoreRemovedInvitation;
  const sentThisVisitIds = props.sentThisVisitIds;

  const confirm = useConfirmDialog();
  const [cancelEntityOfficerInvitation] = useCancelEntityOfficerInvitationMutation();

  async function handleCancelInvite(invitation: EntityOfficerInvitation) {
    const response = await confirm({
      title: "Cancel Invitation?",
      message: "Are you sure you want to cancel this entity officer invitation?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await cancelEntityOfficerInvitation({ invitation }).unwrap();
      onInvitationRemoved(invitation);
      enqueueSnackbar("Canceled entity officer invitation", { variant: "success" });
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        ex instanceof EntityOfficerInvitationNotOpenError
          ? ex.message
          : "Failed to cancel entity officer invitation",
        { variant: "error" },
      );
    }
  }

  return (
    <InvitationsTable>
      <InvitationsTableTitle variant="h6">Entity Officer Invitations</InvitationsTableTitle>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Email Address</TableCell>
            <TableCell align="left">Entity</TableCell>
            <TableCell align="left">Message</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(invitations.length < 1) && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <NoRowsPlaceholder>No Pending Invitations</NoRowsPlaceholder>
              </TableCell>
            </TableRow>
          )}
          {invitations.map((invitation: EntityOfficerInvitation) => {
            const invitationId = invitation.id?.value;
            const sentThisVisit = invitationId ? (sentThisVisitIds?.has(invitationId) ?? false) : false;

            return (
              <InvitationRow
                className={sentThisVisit ? "active-officer-invitation just-sent-officer-invitation" : "active-officer-invitation"}
                key={invitationId}
                data-invitation-id={invitationId}
                sentThisVisit={sentThisVisit}>
                <TableCell align="left">
                  <InviteeEmail>
                    <Typography>{invitation.registrationInfo?.email?.value ?? ''}</Typography>
                    {sentThisVisit && <Chip color="info" label="New" size="small" variant="outlined" />}
                  </InviteeEmail>
                </TableCell>
                <TableCell align="left">
                  <Typography>{invitation.entityName.value}</Typography>
                </TableCell>
                <TableCell align="left">
                  <Typography>{invitation.message?.value ?? <Icon>remove</Icon>}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    onClick={() => handleCancelInvite(invitation)}
                    color="primary">
                    Cancel
                  </Button>
                </TableCell>
              </InvitationRow>
            );
          })}
        </TableBody>
      </Table>
    </InvitationsTable>
  );
}
